import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { SITE_BG } from "../../../dark-theme/palette";
import { decryptText } from "../../../services/CryptoUtil";
import Logger from "../../../services/Logger";
import Item from "../../components/Item";
import BrowseStylePuzzleCard, { BROWSE_SHELF_CARD_WIDTH } from "../../browse-puzzles/BrowseStylePuzzleCard";
import GridPlayMain from "../../puzzles/grid/GridPlayMain";
import { fetchPublishedHowtoStory } from "../fetchPublishedHowtoStory";
import HowToWordSearchDragDemo from "../puzzle-word-search/HowToWordSearchDragDemo";
import StaticToolbar from "./StaticToolbar";
import encryptedSampleDataEn from "./samples/sample_en_grid_encrypted.json";
import encryptedSampleDataFr from "./samples/sample_fr_grid_encrypted.json";
import encryptedWsDataEn from "../puzzle-word-search/samples/sample_story_en_word_search_encrypted.json";
import encryptedWsDataFr from "../puzzle-word-search/samples/sample_story_fr_word_search_encrypted.json";
import wordGridHowToBrowseStories from "./samples/wordGridHowToBrowseStories.json";
import { SUPPORT_FRENCH } from "../../../config/supportFrench";
import { usePagesTranslation } from "../../locales";

/**
 * Hidden-message and word-search how-to (puzzle play only).
 * Component lives under `puzzle-hidden-message/` with samples + static grid UI.
 * Routed via `./HowToWordGrid` entries: `/howto/puzzle/hidden-message`, `/howto/puzzle/word-search`.
 */
export default function WordGridHowTo({ gridVariant = "HIDDEN_MESSAGE" }) {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const [, setSamplePuzzleData] = useState([]);
    const [samplePuzzle, setSamplePuzzle] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState("english");

    const extractEncryptedText = (data) => {
        if (typeof data === "string") {
            return data;
        }
        if (Array.isArray(data)) {
            return data.join("") || data[0];
        }
        if (typeof data === "object" && data !== null) {
            return data.data || data.encrypted || data.text || data.default || Object.values(data)[0];
        }
        return null;
    };

    useEffect(() => {
        let cancelled = false;

        const loadFromEncrypted = async () => {
            if (cancelled) return;
            try {
                const encryptedSampleData = selectedLanguage === "french"
                    ? (gridVariant === "WORD_SEARCH" ? encryptedWsDataFr : encryptedSampleDataFr)
                    : (gridVariant === "WORD_SEARCH" ? encryptedWsDataEn : encryptedSampleDataEn);
                if (!encryptedSampleData) {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }
                const encryptedText = extractEncryptedText(encryptedSampleData);

                if (!encryptedText || typeof encryptedText !== "string") {
                    if (!cancelled) setSamplePuzzle(null);
                    return;
                }

                const decryptedText = decryptText(encryptedText.trim());

                if (decryptedText) {
                    const parsedData = JSON.parse(decryptedText);
                    setSamplePuzzleData(parsedData);

                    let nextPuzzle = null;
                    if (parsedData && Array.isArray(parsedData) && parsedData.length > 0) {
                        nextPuzzle = parsedData[0];
                    } else if (parsedData && !Array.isArray(parsedData)) {
                        nextPuzzle = parsedData;
                    }

                    if (nextPuzzle) {
                        const variantStories =
                            gridVariant === "WORD_SEARCH"
                                ? wordGridHowToBrowseStories.WORD_SEARCH
                                : wordGridHowToBrowseStories.HIDDEN_MESSAGE;
                        const overlayStory =
                            variantStories?.[selectedLanguage === "french" ? "french" : "english"];
                        if (overlayStory) {
                            nextPuzzle = { ...nextPuzzle, story: overlayStory };
                        }
                    }

                    if (!cancelled) setSamplePuzzle(nextPuzzle);

                    if (nextPuzzle) {
                        const liveStory = await fetchPublishedHowtoStory(
                            gridVariant,
                            selectedLanguage === "french" ? "french" : "english",
                        );
                        if (!cancelled && liveStory) {
                            setSamplePuzzle({ ...nextPuzzle, story: liveStory });
                        }
                    }
                } else if (!cancelled) {
                    setSamplePuzzle(null);
                }
            } catch (error) {
                Logger.error("Error decrypting or parsing sample data:", error);
                if (!cancelled) setSamplePuzzle(null);
            }
        };

        loadFromEncrypted();

        return () => {
            cancelled = true;
        };
    }, [selectedLanguage, gridVariant]);

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) {
            setSelectedLanguage(newLanguage);
        }
    };

    const handleCardClick = () => {
        navigate(gridVariant === "WORD_SEARCH" ? "/puzzles/word-search" : "/puzzles/hidden-message");
    };

    const titleKey =
        gridVariant === "WORD_SEARCH" ? "stories.type-word-search" : "stories.type-hidden-message";

    const introP1Key =
        gridVariant === "WORD_SEARCH" ? "howto.wordGrid.intro-puzzle-ws-p1" : "howto.wordGrid.intro-puzzle-hm-p1";

    const introP2Key =
        gridVariant === "WORD_SEARCH" ? "howto.wordGrid.intro-puzzle-ws-p2" : "howto.wordGrid.intro-puzzle-hm-p2";

    const introP2Params = {
        puzzleKind: gridVariant === "WORD_SEARCH" ? t("nav.word-search") : t("nav.hidden-msg"),
    };

    if (!samplePuzzle) {
        return (
            <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography>{t("howto.loading")}</Typography>
            </Box>
        );
    }

    const puzzleCardCategory = gridVariant === "WORD_SEARCH" ? "WORD_SEARCH" : "HIDDEN_MESSAGE";
    const puzzleCardContentLanguage = selectedLanguage === "french" ? "french" : "english";

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Grid container spacing={0} sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Grid size={{ xs: 12 }} sx={{ width: "100%", maxWidth: "100%", padding: 0 }}>
                    <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                            {t(titleKey)}
                        </Typography>
                        {SUPPORT_FRENCH ? (
                        <Box sx={{ mb: 4 }}>
                            <ToggleButtonGroup
                                value={selectedLanguage}
                                exclusive
                                onChange={handleLanguageChange}
                                aria-label="sample puzzle language"
                                sx={{
                                    "& .MuiToggleButton-root": {
                                        "&.Mui-selected": {
                                            borderColor: "#3BA666 !important",
                                            borderWidth: "2px !important",
                                            backgroundColor: "rgba(59, 166, 102, 0.08)",
                                            "&:hover": {
                                                backgroundColor: "rgba(59, 166, 102, 0.12)",
                                            },
                                        },
                                    },
                                }}
                            >
                                <Tooltip
                                    title={t("howto.tooltip-english")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: "0.9rem",
                                                padding: "8px 12px",
                                                backgroundColor: "#3BA666",
                                                fontWeight: 500,
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            },
                                        },
                                        arrow: { sx: { color: "#3BA666" } },
                                    }}
                                >
                                    <ToggleButton value="english" aria-label="english">
                                        {t("howto.english-example")}
                                    </ToggleButton>
                                </Tooltip>
                                <Tooltip
                                    title={t("howto.tooltip-french")}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: "0.9rem",
                                                padding: "8px 12px",
                                                backgroundColor: "#3BA666",
                                                fontWeight: 500,
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                            },
                                        },
                                        arrow: { sx: { color: "#3BA666" } },
                                    }}
                                >
                                    <ToggleButton value="french" aria-label="french">
                                        {t("howto.french-example")}
                                    </ToggleButton>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Box>
                        ) : null}

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.wordGrid.intro-title")}
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t(introP1Key)}
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t(introP2Key, introP2Params)}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.puzzle-card-title")}
                            </Typography>
                            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                                <Box
                                    sx={{
                                        width: BROWSE_SHELF_CARD_WIDTH,
                                        maxWidth: "100%",
                                        flexShrink: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <Tooltip
                                        title={t("howto.crossword.card-dive-tooltip")}
                                        arrow
                                        placement="top"
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    fontSize: "0.95rem",
                                                    padding: "10px 14px",
                                                    backgroundColor: "#3BA666",
                                                    fontWeight: 500,
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                                },
                                            },
                                            arrow: { sx: { color: "#3BA666" } },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                cursor: "pointer",
                                                transition: "transform 0.2s ease-in-out",
                                                "&:hover": { transform: "translateY(-4px)" },
                                            }}
                                        >
                                            <BrowseStylePuzzleCard
                                                category={puzzleCardCategory}
                                                puzzle={samplePuzzle}
                                                storyTeaserRow={samplePuzzle}
                                                contentLanguage={puzzleCardContentLanguage}
                                                lang={lang}
                                                t={t}
                                                userPayPlan="FREE"
                                                buttonText={t("howto.play")}
                                                forceUnlock={true}
                                                onPlayClick={handleCardClick}
                                                hideWordCountChip
                                                hideClusteredBubbles
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.card-displays")}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-free-standard")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.wordGrid.card-title-hint")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-level")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-words")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.card-published")}</Typography></li>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        {gridVariant === "WORD_SEARCH" ? (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                    {t("howto.wordGrid.grid-title", "Puzzle grid")}
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                    {t(
                                        "howto.wordGrid.grid-desc-ws",
                                        "Word Search boards come in many sizes — Mini 6×6 through Classic 12×12. Words can be found in three directions:"
                                    )}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                                    <li>
                                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                            {t("howto.wordGrid.grid-horizontal")}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                            {t("howto.wordGrid.grid-vertical")}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                            {t("howto.wordGrid.grid-diagonal")}
                                        </Typography>
                                    </li>
                                </Box>
                                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                    {t("howto.wordGrid.grid-reversed")}
                                </Typography>

                                <Typography
                                    variant="h6"
                                    component="h3"
                                    gutterBottom
                                    sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}
                                >
                                    {t("howto.wordGrid.drag-title", "Select words by dragging")}
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                    {t(
                                        "howto.wordGrid.drag-desc",
                                        "Press and hold the first letter, then drag in a straight line across the word. Release to confirm."
                                    )}
                                </Typography>
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "rgba(59, 166, 102, 0.06)",
                                        border: "1px solid",
                                        borderColor: "rgba(59, 166, 102, 0.25)",
                                    }}
                                >
                                    <HowToWordSearchDragDemo />
                                </Box>
                            </Box>
                        ) : null}

                        {gridVariant === "WORD_SEARCH" ? <Divider sx={{ my: 4 }} /> : null}

                        <Box id="play-main" sx={{ mb: 5, scrollMarginTop: "5rem" }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.crossword.play-main-title")}
                            </Typography>
                            {SUPPORT_FRENCH && selectedLanguage === "french" && (
                                <Alert severity="info" sx={{ mb: 3, maxWidth: "100%" }}>
                                    <Typography variant="body2" sx={{ fontSize: "0.95rem" }}>
                                        {t("howto.wordGrid.char-norm-info")}
                                    </Typography>
                                </Alert>
                            )}
                            {gridVariant === "WORD_SEARCH" ? (
                                <Alert severity="success" sx={{ mb: 3, maxWidth: "100%" }}>
                                    <Typography variant="body2" sx={{ fontSize: "0.95rem" }}>
                                        {t(
                                            "howto.wordGrid.drag-demo-caption",
                                            "Press the first letter, then drag across the word — release to mark it found. Try it on the live sample below."
                                        )}
                                    </Typography>
                                </Alert>
                            ) : null}
                            <Box sx={{ mb: 3, width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                                <GridPlayMain
                                    key={`howto-grid-${gridVariant}-${selectedLanguage}`}
                                    gridTplId="howto-embedded-grid-sample"
                                    gridPlayRouteTypeSegment={
                                        gridVariant === "WORD_SEARCH" ? "word_search" : "hidden_message"
                                    }
                                    howtoEmbed
                                    howtoSamplePuzzle={samplePuzzle}
                                />
                            </Box>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.play-main-parts")}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.part-title")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.part-grid")}</Typography></li>
                                <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.wordGrid.part-words")}</Typography></li>
                            </Box>

                            <Divider sx={{ my: 4 }} />

                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                    {t("howto.crossword.toolbar-title")}
                                </Typography>
                                <Box sx={{ mb: 3, display: "flex", justifyContent: "center", width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                                    <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                                        <StaticToolbar
                                            puzzleTitle={samplePuzzle?.title || "Sample Puzzle"}
                                            puzzleId={samplePuzzle?.id || samplePuzzle?.templateId || "sample-id"}
                                            puzzleType={gridVariant === "WORD_SEARCH" ? "WORD_SEARCH" : "HIDDEN_MESSAGE"}
                                        />
                                    </Box>
                                </Box>
                                <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                    {t("howto.crossword.toolbar-intro")}
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-back")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.wordGrid.btn-bold")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.wordGrid.btn-pdf")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-share")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-clear")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.crossword.btn-full")}</Typography></li>
                                    <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.wordGrid.btn-wordlist")}</Typography></li>
                                </Box>
                            </Box>
                        </Box>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}
